import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { Science, Add, Save } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useTiposEstudios, claveCanal } from '../hooks/useTiposEstudios';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const MODALIDADES = [
  { value: 'D', label: 'DOM', desc: 'Domicilio' },
  { value: 'I', label: 'INT', desc: 'Internado' },
  { value: 'A', label: 'AMB', desc: 'Ambulatorio' },
];

const FORM_INICIAL = { tipoEstudio: '', estudio: '', idCategoria: '', forma: 'D', diasEntrega: '', habilitado: 'S' };

const GrillaTiposEstudios = () => {
  const { tiposEstudios, categorias, canalesPorEstudio, opcionesCanales, loading, cargar, guardar, actualizar, eliminar, guardarCanales } = useTiposEstudios();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  // Canales que puede usar el tipo de estudio (solo en edición): claves "canales-conCpap".
  const [canalesSel, setCanalesSel] = useState([]);

  const textoCanales = (id) => (canalesPorEstudio[id] || []).map(c => c.etiqueta).join(', ');
  const toggleCanal = (clave) => setCanalesSel(prev =>
    prev.includes(clave) ? prev.filter(c => c !== clave) : [...prev, clave]
  );
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => { setForm(FORM_INICIAL); setModal({ mode: 'nuevo' }); };
  const abrirEditar = (item) => {
    setForm({
      tipoEstudio: item.tipoEstudio || '',
      estudio: item.estudio || '',
      idCategoria: item.idCategoria ?? '',
      forma: item.forma || 'D',
      diasEntrega: item.diasEntrega ?? '',
      habilitado: item.habilitado || 'S',
    });
    setCanalesSel((canalesPorEstudio[item.idTipoEstudio] || []).map(claveCanal));
    setModal({ mode: 'editar', item });
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    const body = {
      TipoEstudio: form.tipoEstudio,
      Estudio: form.estudio,
      IdCategoria: form.idCategoria === '' ? null : parseInt(form.idCategoria),
      Forma: form.forma,
      DiasEntrega: form.diasEntrega === '' ? null : parseInt(form.diasEntrega),
      Habilitado: form.habilitado,
    };
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.idTipoEstudio, body);
        const canales = opcionesCanales
          .filter(o => canalesSel.includes(claveCanal(o)))
          .map(o => ({ canales: o.canales, conCpap: o.conCpap }));
        await guardarCanales(modal.item.idTipoEstudio, canales);
        setToast({ message: 'Tipo de práctica actualizado con éxito.', type: 'success' });
      } else {
        await guardar(body);
        setToast({ message: 'Tipo de práctica guardado con éxito.', type: 'success' });
      }
      setModal(null);
      cargar();
    } catch {
      setToast({ message: 'Error al guardar.', type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(confirmEliminar.id);
      setToast({ message: 'Tipo de práctica eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Science sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Tipos de Práctica
          </h2>
          <span className="page-subtitle">Gestión del catálogo de tipos de práctica médica</span>
        </div>

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Tipo de Práctica' : 'Nuevo Tipo de Práctica'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.75rem' }}>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Código de Práctica *</Form.Label>
                    <Form.Control value={form.tipoEstudio} onChange={(e) => setForm(f => ({ ...f, tipoEstudio: e.target.value }))} maxLength={100} placeholder="Código..." required autoFocus />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Práctica Médica *</Form.Label>
                    <Form.Control value={form.estudio} onChange={(e) => setForm(f => ({ ...f, estudio: e.target.value }))} maxLength={100} placeholder="Nombre..." required />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Categoría</Form.Label>
                    <Form.Select value={form.idCategoria} onChange={(e) => setForm(f => ({ ...f, idCategoria: e.target.value }))}>
                      <option value="">Sin categoría</option>
                      {categorias.map(c => <option key={c.idCategoria} value={c.idCategoria}>{c.categoria1}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Días de Entrega *</Form.Label>
                    <Form.Control type="number" min={0} max={999} value={form.diasEntrega} onChange={(e) => setForm(f => ({ ...f, diasEntrega: e.target.value }))} placeholder="Nº días..." required />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Modalidad *</Form.Label>
                    <Form.Select value={form.forma} onChange={(e) => setForm(f => ({ ...f, forma: e.target.value }))}>
                      {MODALIDADES.map(m => <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Habilitado *</Form.Label>
                    <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.25rem' }}>
                      {[{ value: 'S', label: 'Sí' }, { value: 'N', label: 'No' }].map(h => (
                        <Button
                          key={h.value}
                          type="button"
                          size="sm"
                          variant={form.habilitado === h.value ? (h.value === 'S' ? 'success' : 'danger') : 'outline-secondary'}
                          onClick={() => setForm(f => ({ ...f, habilitado: h.value }))}
                          style={{ minWidth: '70px' }}
                        >
                          {h.label}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                </div>
                <div className="col-md-12">
                  <fieldset className="canales-tipo-estudio" aria-label="Canales que puede usar el estudio">
                    <legend className="form-label mb-1">Canales que puede usar</legend>
                    {modal?.mode !== 'editar' ? (
                      <Form.Text muted>Guardá el tipo de práctica para elegir sus canales.</Form.Text>
                    ) : opcionesCanales.length === 0 ? (
                      <Form.Text muted>No hay equipos cargados para armar los canales.</Form.Text>
                    ) : (
                      <div className="d-flex flex-wrap gap-3">
                        {opcionesCanales.map(o => (
                          <Form.Check
                            key={claveCanal(o)}
                            type="checkbox"
                            id={`canal-tipo-estudio-${claveCanal(o)}`}
                            label={o.etiqueta}
                            checked={canalesSel.includes(claveCanal(o))}
                            onChange={() => toggleCanal(claveCanal(o))}
                          />
                        ))}
                      </div>
                    )}
                    {modal?.mode === 'editar' && (
                      <Form.Text muted className="d-block">
                        Se usan al cargar los horarios de los técnicos. Si no marcás ninguno, el estudio puede usar cualquier canal.
                      </Form.Text>
                    )}
                  </fieldset>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.tipoEstudio.trim() || !form.estudio.trim()}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{tiposEstudios.length} tipos de práctica</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Práctica Médica</th>
                  <th>Categoría</th>
                  <th>Modalidad</th>
                  <th>Días Entrega</th>
                  <th>Habilitado</th>
                  <th>Canales</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tiposEstudios.length === 0 ? (
                  <tr><td colSpan={8} className="empty-state">{loading ? 'Cargando...' : 'No hay tipos de práctica disponibles'}</td></tr>
                ) : tiposEstudios.map((t) => (
                  <tr key={t.idTipoEstudio}>
                    <td>{t.tipoEstudio}</td>
                    <td>{t.estudio}</td>
                    <td>{t.categoria || '-'}</td>
                    <td>{t.forma}</td>
                    <td style={{ textAlign: 'center' }}>{t.diasEntrega}</td>
                    <td style={{ textAlign: 'center' }}>{t.habilitado}</td>
                    <td>{textoCanales(t.idTipoEstudio) || 'Cualquiera'}</td>
                    <td className="action-cell">
                      <RowActions onEdit={() => abrirEditar(t)} onDelete={() => setConfirmEliminar({ id: t.idTipoEstudio, label: t.tipoEstudio })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar Tipo de Práctica"
        message={`¿Eliminar el tipo de práctica "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaTiposEstudios;
